import { PlateEditor, Value, ELEMENT_PARAGRAPH } from "@udecode/plate";
import { ATTICUS_CLIPBOARD_CUSTOM_FORMAT } from "../../custom-clipboard";
import { Editor, Element } from "slate";
import { RichText } from "../../../config/typescript";
import { shouldShowPasteFormattingPopup, showPasteFormattingPopup } from "../helpers/pasteFormattingPopup";


/**
 * Represents a text segment with formatting information
 */
interface FormattedTextSegment {
    text: string;
    bold?: boolean;
    italic?: boolean;
}

/**
 * Extracts text content with formatting information from HTML.
 * Returns an array of text segments grouped by paragraphs.
 */
const extractFormattedTextSegments = (htmlString: string): FormattedTextSegment[][] => {
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, "text/html");
        
        const paragraphs: FormattedTextSegment[][] = [];
        
        const walkAndExtractSegments = (node: Node, inheritedBold = false, inheritedItalic = false): FormattedTextSegment[] => {
            const segments: FormattedTextSegment[] = [];
            
            if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent?.replace(/[\r\n\t]+/g, " ") || "";
                if (text) {
                    segments.push({
                        text,
                        bold: inheritedBold,
                        italic: inheritedItalic
                    });
                }
                return segments;
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement;
                const tagName = el.tagName.toLowerCase();

                // Skip <style> tags so that CSS content inside them is not pasted as plain text
                if(tagName === "style"){
                    return [];
                }

                // Handle <br> tags by adding a newline character
                if (tagName === "br") {
                    segments.push({
                        text: "\n",
                        bold: inheritedBold,
                        italic: inheritedItalic
                    });
                    return segments;
                }
                
                // If this is a block element, process it separately
                if (isBlockElement(tagName)) {
                    const blockSegments: FormattedTextSegment[] = [];
                    Array.from(el.childNodes).forEach(child => {
                        blockSegments.push(...walkAndExtractSegments(child, inheritedBold, inheritedItalic));
                    });
                    if (blockSegments.length > 0) {
                        paragraphs.push(blockSegments);
                    }
                    return [];
                }
                
                // Determine current element's formatting
                const inlineWeight = el.style.fontWeight;
                const inlineStyle = el.style.fontStyle;
                const computedStyle = window.getComputedStyle(el);
                const computedWeight = computedStyle.fontWeight;
                const computedFontStyle = computedStyle.fontStyle;
                
                let isBold = inheritedBold;
                let isItalic = inheritedItalic;
                
                // Check for bold
                if (tagName === "strong" || tagName === "b") {
                    isBold = !(inlineWeight === "normal" || inlineWeight === "400");
                } else if (!inheritedBold) {
                    isBold = Boolean(computedWeight === "bold" || computedWeight === "bolder" || 
                             computedWeight === "700" || inlineWeight === "700" ||
                             (computedWeight && !isNaN(parseInt(computedWeight)) && parseInt(computedWeight) >= 600) ||
                             (inlineWeight && !isNaN(parseInt(inlineWeight)) && parseInt(inlineWeight) >= 600));
                }
                
                // Check for italic
                if (tagName === "em" || tagName === "i") {
                    isItalic = Boolean(inlineStyle !== "normal");
                } else if (!inheritedItalic) {
                    isItalic = computedFontStyle === "italic" || inlineStyle === "italic";
                }
                
                // Process children with current formatting
                Array.from(el.childNodes).forEach(child => {
                    segments.push(...walkAndExtractSegments(child, isBold, isItalic));
                });
            }
            
            return segments;
        };
        
        // Process the root element's children
        const rootSegments: FormattedTextSegment[] = [];
        Array.from(doc.body.childNodes).forEach(child => {
            rootSegments.push(...walkAndExtractSegments(child));
        });
        
        if (rootSegments.length > 0) {
            paragraphs.push(rootSegments);
        }
        
        return paragraphs;
        
    } catch (error) {
        console.error("Error extracting formatted text segments:", error);
        return [];
    }
};

/**
 * Creates Plate text nodes from formatted text segments
 */
const createPlateTextNodes = (segments: FormattedTextSegment[]): RichText[] => {
    return segments.map(segment => {
        const textNode: RichText = { text: segment.text };
        
        if (segment.bold) {
            textNode.bold = true;
        }
        if (segment.italic) {
            textNode.italic = true;
        }
        
        return textNode;
    });
};

/**
 * Checks if a paragraph segment contains only whitespace
 */
const isWhitespaceOnlySegment = (segments: FormattedTextSegment[]): boolean => {
    const combinedText = segments.map(segment => segment.text).join("");
    return combinedText.trim() === "";
};

/**
 * Creates Plate paragraph elements from formatted text segments
 * Filters out segments that contain only whitespace
 */
const createPlateParagraphs = (paragraphSegments: FormattedTextSegment[][]): Element[] => {
    return paragraphSegments
        .filter(segments => !isWhitespaceOnlySegment(segments))
        .map(segments => ({
            type: ELEMENT_PARAGRAPH,
            children: createPlateTextNodes(segments)
        }));
};

// block element types
const BLOCK_ELEMENTS = [
    "p", "div", "h1", "h2", "h3", "h4", "h5", "h6",
    "li", "blockquote", "article", "section", "header", "footer",
    "ul", "ol", "td"
];

/**
 * Checks if an element is a block-level element
 * To add new block types, update BLOCK_ELEMENTS array above
 */
const isBlockElement = (tagName: string): boolean => {
    return BLOCK_ELEMENTS.includes(tagName);
};

const ATTICUS_IDENTIFIER = "data-atticus-fragment";

/**
 * Internal helper function to extract Atticus fragment data from clipboard
 * Returns the fragment data if found, null otherwise
 */
const extractAtticusFragment = (data: DataTransfer): string | null => {
    // First try custom format
    const isCopiedFromAtticus = data.types.includes(ATTICUS_CLIPBOARD_CUSTOM_FORMAT);

    if (isCopiedFromAtticus) {
        const customData = data.getData(ATTICUS_CLIPBOARD_CUSTOM_FORMAT);
        if (customData) {
            return customData;
        }
    }

    // Fallback to checking HTML for atticus identifier
    const htmlData = data.getData("text/html");
    if (!htmlData) {
        return null;
    }

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlData;

    // Look for atticus custom attribute in any element
    const elementWithAtticus = tempDiv.querySelector(`[${ATTICUS_IDENTIFIER}]`);
    if (elementWithAtticus) {
        return elementWithAtticus.getAttribute(ATTICUS_IDENTIFIER);
    }

    // Also check if the root element has the attribute
    const rootElement = tempDiv.firstElementChild;
    if (rootElement?.hasAttribute(ATTICUS_IDENTIFIER)) {
        return rootElement.getAttribute(ATTICUS_IDENTIFIER);
    }

    return null;
};

/**
 * Extract Atticus fragment data from clipboard
 * Returns the fragment data if found, null otherwise
 */
export const getAtticusFragmentFromClipboard = (data: DataTransfer): string | null => {
    return extractAtticusFragment(data);
};




/**
 * Enhanced editor with custom clipboard handling.
 * 
 * This function extracts formatting information from external HTML sources and creates
 * Plate text nodes directly, preserving only bold and italic formatting while sanitizing
 * 
 * Features:
 * - Preserves bold/italic formatting (both semantic tags and CSS styles)
 * - Creates proper Plate text nodes with marks
 */
export const withCombinedDeserialization = (editor: PlateEditor<Value> & Editor) => {
    const { insertData } = editor;

    editor.insertData = (data: DataTransfer) => {

        // Handle external HTML content (extract formatting and create Plate nodes)
        const html = data.getData("text/html");
        const plainText = data.getData("text/plain");

        // Check if content is copied from Atticus
        const atticusFragmentData = getAtticusFragmentFromClipboard(data);

        if (atticusFragmentData) {
            try {
                const decoded = decodeURIComponent(window.atob(atticusFragmentData));
                const fragments = JSON.parse(decoded);
                editor.insertFragment(fragments);
                return;
            } catch (error) {
                console.error("Failed to paste Atticus fragment:", error);
            }
        }

        const hasHtmlTags = html && /<[^>]*>/g.test(html);

        if (hasHtmlTags) {
            try {
                // Extract formatted text segments from HTML
                const paragraphSegments = extractFormattedTextSegments(html);

                if (paragraphSegments.length > 0) {
                    // Show formatting popup for external content (if not disabled)
                    const showPopup = shouldShowPasteFormattingPopup();
                    
                    const processContent = () => {
                      // Create Plate nodes and insert using insertFragment
                      const plateNodes = createPlateParagraphs(paragraphSegments);

                      editor.insertFragment(plateNodes);
                      return;
                    };

                    // Show popup if needed, then process content
                    if (showPopup) {
                        showPasteFormattingPopup().then(() => {
                            processContent();
                        });
                    } else {
                        processContent();
                    }
                    return;
                }
            } catch (error) {
                console.error("Error processing HTML content:", error);
            }
        }
        
        // Fall back to plain text insertion for content without formatting
        if (plainText) {
            const newData = new DataTransfer();
            newData.setData("text/plain", plainText);
            insertData(newData);
            return;
        }
        
        insertData(data);
    };

    return editor;
};